import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    BrandMark
} from "./brand";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    MarkdownEditor,
    RadioButton,
    RadioGroup,
    Accordion,
    Separator,
    Progress,
    Avatar,
    Button,
    Badge,
    Alert,
    Empty,
    Item,
    Card,
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    MessageSquare,
    ChevronRight,
    CircleCheck,
    ChevronLeft,
    CirclePlay,
    ArrowLeft,
    RotateCcw,
    Maximize,
    Captions,
    Volume2,
    Pause,
    Lock,
    Play
} from "lucide-react-native";
import Svg, {
    Polyline,
    Circle,
    Line,
    Rect
} from "react-native-svg";
import {
    createKitIcon
} from "../components/kitIcons";

const BackIcon = createKitIcon(ArrowLeft);

const PlayIcon = createKitIcon(Play);

const PauseIcon = createKitIcon(Pause);

const ReplayIcon = createKitIcon(RotateCcw);

const VolumeIcon = createKitIcon(Volume2);

const CaptionsIcon = createKitIcon(Captions);

const FullscreenIcon = createKitIcon(Maximize);

const PreviousIcon = createKitIcon(ChevronLeft);

const NextIcon = createKitIcon(ChevronRight);

const LockIcon = createKitIcon(Lock);

const CircleCheckIcon = createKitIcon(CircleCheck);

const CirclePlayIcon = createKitIcon(CirclePlay);

const QuestionsIcon = createKitIcon(MessageSquare);

const TABS = [
    "overview",
    "notes",
    "questions",
    "resources"
];

const TAB_COUNTS: Record<string, number | undefined> = {
    questions: 12,
    notes: 4
};

const MODULES = [
    {
        lessons: 4,
        done: 4,
        id: "1"
    },
    {
        lessons: 4,
        done: 4,
        id: "2"
    },
    {
        lessons: 5,
        done: 1,
        id: "3"
    },
    {
        lessons: 4,
        done: 0,
        id: "4"
    },
    {
        lessons: 4,
        done: 0,
        id: "5"
    }
];

const MODULE_THREE_MINUTES = [
    18,
    24,
    16,
    9,
    22
];

const GENERIC_MINUTES = 15;

const CURRENT_LESSON = "3.2";

const CHAPTER_WEIGHTS = [
    3,
    4,
    5,
    2,
    2
];

const WATCHED_CHAPTERS = 2.35;

const SPEEDS = [
    1,
    1.25,
    1.5
];

const CHOICES = [
    "line",
    "pie",
    "scatter",
    "donut"
];

const CORRECT_CHOICE = "line";

const COURSE_PERCENT = 42;

const VIDEO_VIEWBOX = "0 0 160 90";

const GRID_LINES = [
    22,
    44,
    66
];

const BARS = [
    {
        height: 22,
        x: 98
    },
    {
        height: 36,
        x: 109
    },
    {
        height: 48,
        x: 120
    },
    {
        height: 60,
        x: 131
    }
];

const TREND_POINTS = "16,66 34,60 52,56 70,56 86,50 104,36 124,24 142,16";

const stylesheet = StyleSheet.create({
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    controls: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    lesson: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center"
    },
    overlay: {
        justifyContent: "space-between",
        position: "absolute",
        bottom: 0,
        right: 0,
        left: 0,
        top: 0
    },
    footer: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    centered: {
        pointerEvents: "box-none",
        justifyContent: "center",
        position: "absolute",
        alignItems: "center",
        bottom: 0,
        right: 0,
        left: 0,
        top: 0
    },
    row: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    watched: {
        position: "absolute",
        height: "100%",
        left: 0
    },
    chapter: {
        position: "relative",
        overflow: "hidden"
    },
    video: {
        aspectRatio: 16 / 9,
        overflow: "hidden",
        width: "100%"
    },
    chapters: {
        flexDirection: "row"
    },
    columns: {
        alignSelf: "center",
        width: "100%",
        flexGrow: 1
    },
    scroll: {
        minHeight: "100%"
    },
    cell: {
        flexShrink: 1,
        minWidth: 0
    },
    main: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    editor: {
        width: "100%"
    },
    flex: {
        flexBasis: 0,
        flexGrow: 1,
        minWidth: 0
    },
    side: {
        flexGrow: 0
    }
});

const handlePress = () => undefined;

const Course01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        shapeConfigs,
        activeTheme,
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        tab,
        setTab
    ] = useState("notes");

    const [
        isPlaying,
        setIsPlaying
    ] = useState(false);

    const [
        speedIndex,
        setSpeedIndex
    ] = useState(1);

    const [
        openModule,
        setOpenModule
    ] = useState<string | Array<string>>("3");

    const [
        lessonId,
        setLessonId
    ] = useState(CURRENT_LESSON);

    const [
        answer,
        setAnswer
    ] = useState<string | undefined>("line");

    const [
        isChecked,
        setIsChecked
    ] = useState(false);

    const isWide = width >= shapeConfigs.wideBreakpoint;

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const videoTheme = activeTheme === "dark" ? "light" : "dark";

    const customVideoTheme = {
        themeKey: videoTheme
    } as const;

    const numberFormat = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 2
    });

    const percentFormat = new Intl.NumberFormat(activeLocale, {
        style: "percent"
    });

    const getLessonMinutes = (module: string, index: number) => module === "3" ? MODULE_THREE_MINUTES[index] ?? GENERIC_MINUTES : GENERIC_MINUTES;

    const getLessonTitle = (module: string, index: number) => {
        if(module === "3") {
            return t(`blocks.course01.lesson_3_${index + 1}`);
        }

        return t("blocks.course01.lessonGeneric", [
            module,
            index + 1
        ]);
    };

    const getLessonState = (module: typeof MODULES[number], index: number) => {
        if(`${module.id}.${index + 1}` === lessonId) {
            return "current";
        }

        if(index < module.done) {
            return "done";
        }

        if(module.id === "3" && index === module.done) {
            return "next";
        }

        return module.done === module.lessons || Number(module.id) < 3 ? "done" : "locked";
    };

    const handleSpeedPress = () => setSpeedIndex((speedIndex + 1) % SPEEDS.length);

    const handlePlayToggle = () => setIsPlaying(!isPlaying);

    const handleTabChange = (value: string) => setTab(value);

    const handleModuleChange = (value: string | Array<string>) => setOpenModule(value);

    const handleChoiceChange = (value: string) => {
        setAnswer(value);

        setIsChecked(false);
    };

    const handleCheck = () => setIsChecked(true);

    const handleLessonPress = (id: string) => () => setLessonId(id);

    const [
        currentModule = "3",
        currentIndex = "2"
    ] = lessonId.split(".");

    const currentTitle = getLessonTitle(currentModule, Number(currentIndex) - 1);

    const renderLesson = (module: typeof MODULES[number]) => (_: unknown, index: number) => {
        const state = getLessonState(module, index);

        const id = `${module.id}.${index + 1}`;

        const isLocked = state === "locked";

        return <Item
            onPress={isLocked ? undefined : handleLessonPress(id)}
            isSelected={state === "current"}
            isDisabled={isLocked}
            variant="plain"
            size="xSmall"
            key={id}
        >
            <Item.Media
                icon={isLocked ? LockIcon : state === "done" ? CircleCheckIcon : CirclePlayIcon}
                variant="icon"
            />
            <Item.Content>
                <Item.Title>
                    {`${id} ${getLessonTitle(module.id, index)}`}
                </Item.Title>
            </Item.Content>
            <Item.Actions>
                <Text
                    variant="bodySmallSize"
                    color="mid"
                >
                    {t("blocks.course01.minutes", [
                        getLessonMinutes(module.id, index)
                    ])}
                </Text>
            </Item.Actions>
        </Item>;
    };

    const renderModule = (module: typeof MODULES[number]) => <Accordion.Item
        value={module.id}
        key={module.id}
    >
        <Accordion.Trigger
            title={`${module.id}  ${t(`blocks.course01.module_${module.id}`)}`}
            rightContent={`${module.done} / ${module.lessons}`}
        />
        <Accordion.Content>
            {Array.from({
                length: module.lessons
            }).map(renderLesson(module))}
        </Accordion.Content>
    </Accordion.Item>;

    const renderChapter = (weight: number, index: number) => {
        const fill = Math.min(Math.max(WATCHED_CHAPTERS - index, 0), 1);

        return <View
            style={[
                stylesheet.chapter,
                {
                    backgroundColor: colors.content.border.default,
                    borderRadius: radiuses.full,
                    height: spaces.spacingXs,
                    flex: weight
                }
            ]}
            key={`${weight}-${index}`}
        >
            <View
                style={[
                    stylesheet.watched,
                    {
                        backgroundColor: colors.content.container.default,
                        width: `${fill * 100}%`
                    }
                ]}
            />
        </View>;
    };

    const renderChoice = (key: string) => <RadioButton
        title={t(`blocks.course01.choice_${key}`)}
        spreadBehaviour="stretch"
        variant="card"
        value={key}
        key={key}
    />;

    const renderQuizResult = () => {
        if(!isChecked) {
            return null;
        }

        const isCorrect = answer === CORRECT_CHOICE;

        return <Alert
            description={t(isCorrect ? "blocks.course01.correctDescription" : "blocks.course01.wrongDescription")}
            title={t(isCorrect ? "blocks.course01.correct" : "blocks.course01.wrong")}
            type={isCorrect ? "success" : "danger"}
        />;
    };

    const renderVideo = () => <View
        style={[
            stylesheet.video,
            {
                backgroundColor: colors.content.container.inverse,
                borderRadius: radiuses.surface
            }
        ]}
    >
        <Svg
            preserveAspectRatio="xMidYMid slice"
            viewBox={VIDEO_VIEWBOX}
            height="100%"
            width="100%"
        >
            {
                GRID_LINES.map((y) => <Line
                    stroke={colors.content.border.default}
                    strokeWidth={0.3}
                    x2={144}
                    x1={16}
                    key={y}
                    y1={y}
                    y2={y}
                />)
            }
            {
                BARS.map((bar) => <Rect
                    fill={colors.chart.categorical["1"]}
                    y={72 - bar.height}
                    height={bar.height}
                    key={bar.x}
                    x={bar.x}
                    width={8}
                />)
            }
            <Polyline
                stroke={colors.chart.categorical["2"]}
                strokeLinejoin="round"
                points={TREND_POINTS}
                strokeLinecap="round"
                strokeWidth={1.2}
                fill="none"
            />
            <Circle
                fill={colors.chart.categorical["2"]}
                cx={142}
                r={2.2}
                cy={16}
            />
        </Svg>
        <View
            style={[
                stylesheet.overlay,
                {
                    padding: isCompact ? spaces.spacingSm : spaces.spacingMd
                }
            ]}
        >
            <View>
                <Text
                    customTheme={customVideoTheme}
                    variant="bodySmallSize"
                    color="mid"
                >
                    {t("blocks.course01.videoKicker")}
                </Text>
                <Text
                    variant={isCompact ? "titleMediumSize" : "displaySmallSize"}
                    customTheme={customVideoTheme}
                    color="high"
                >
                    {currentTitle}
                </Text>
            </View>
            <View
                style={{
                    gap: spaces.spacingXs
                }}
            >
                <View
                    style={[
                        stylesheet.chapters,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    {CHAPTER_WEIGHTS.map(renderChapter)}
                </View>
                <View
                    style={stylesheet.controls}
                >
                    <View
                        style={[
                            stylesheet.row,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Button
                            accessibilityLabel={t(isPlaying ? "blocks.course01.pause" : "blocks.course01.play")}
                            icon={isPlaying ? PauseIcon : PlayIcon}
                            customTheme={customVideoTheme}
                            onPress={handlePlayToggle}
                            spreadBehaviour="free"
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                        <Button
                            accessibilityLabel={t("blocks.course01.replay")}
                            customTheme={customVideoTheme}
                            spreadBehaviour="free"
                            onPress={handlePress}
                            icon={ReplayIcon}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                        <Button
                            accessibilityLabel={t("blocks.course01.volume")}
                            customTheme={customVideoTheme}
                            spreadBehaviour="free"
                            onPress={handlePress}
                            icon={VolumeIcon}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                        <Text
                            customTheme={customVideoTheme}
                            variant="monoMediumSize"
                            color="high"
                        >
                            {t("blocks.course01.time")}
                        </Text>
                    </View>
                    <View
                        style={[
                            stylesheet.row,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <Button
                            title={t("blocks.course01.speed", [
                                numberFormat.format(SPEEDS[speedIndex] ?? 1)
                            ])}
                            customTheme={customVideoTheme}
                            onPress={handleSpeedPress}
                            spreadBehaviour="free"
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                        <Button
                            accessibilityLabel={t("blocks.course01.captions")}
                            customTheme={customVideoTheme}
                            spreadBehaviour="free"
                            onPress={handlePress}
                            icon={CaptionsIcon}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                        <Button
                            accessibilityLabel={t("blocks.course01.fullscreen")}
                            customTheme={customVideoTheme}
                            spreadBehaviour="free"
                            icon={FullscreenIcon}
                            onPress={handlePress}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                    </View>
                </View>
            </View>
        </View>
        {
            isPlaying ?
                null
            :
                <View
                    style={stylesheet.centered}
                >
                    <Button
                        accessibilityLabel={t("blocks.course01.play")}
                        customTheme={customVideoTheme}
                        onPress={handlePlayToggle}
                        spreadBehaviour="free"
                        variant="filled"
                        icon={PlayIcon}
                        type="neutral"
                        size="large"
                    />
                </View>
        }
    </View>;

    const renderTabContent = () => {
        if(tab === "notes") {
            return <MarkdownEditor
                initialValue={t("blocks.course01.notesText")}
                style={stylesheet.editor}
                spreadBehaviour="stretch"
                defaultMode="preview"
            />;
        }

        if(tab === "overview") {
            return <Text
                variant="readingMediumSize"
                color="high"
            >
                {t("blocks.course01.overviewText")}
            </Text>;
        }

        return <Empty
            variant="outline"
        >
            <Empty.Header>
                <Empty.Media
                    icon={QuestionsIcon}
                />
                <Empty.Title>
                    {t(`blocks.course01.tab_${tab}`)}
                </Empty.Title>
                <Empty.Description>
                    {t("blocks.course01.otherDescription")}
                </Empty.Description>
            </Empty.Header>
        </Empty>;
    };

    return <ScrollView
        contentContainerStyle={stylesheet.scroll}
    >
        <View
            style={[
                stylesheet.header,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.row,
                    stylesheet.cell,
                    {
                        gap: spaces.spacingSm,
                        flexWrap: "nowrap"
                    }
                ]}
            >
                <Button
                    accessibilityLabel={t("blocks.course01.back")}
                    onPress={handlePress}
                    variant="ghost"
                    icon={BackIcon}
                    type="neutral"
                    size="small"
                />
                <BrandMark/>
                {
                    isCompact ?
                        null
                    :
                        <>
                            <Separator
                                length={spaces.spacingLg}
                                orientation="vertical"
                            />
                            <Text
                                variant="titleSmallSize"
                                numberOfLines={1}
                                color="high"
                            >
                                {t("blocks.course01.courseTitle")}
                            </Text>
                        </>
                }
            </View>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="bodySmallSize"
                    color="mid"
                >
                    {t("blocks.course01.lessonCount")}
                </Text>
                <Avatar
                    title={t("blocks.course01.student")}
                    size="xSmall"
                />
            </View>
        </View>
        <Separator/>
        <View
            style={[
                stylesheet.columns,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingXl
                }
            ]}
        >
            <View
                style={{
                    alignItems: isWide ? "flex-start" : undefined,
                    flexDirection: isWide ? "row" : "column",
                    gap: spaces.spacingMd
                }}
            >
                <View
                    style={[
                        stylesheet.main,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    {renderVideo()}
                    <View
                        style={[
                            stylesheet.header,
                            stylesheet.row,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.cell,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <Text
                                variant="headlineMediumSize"
                                accessibilityRole="header"
                                color="high"
                            >
                                {`${lessonId} ${currentTitle}`}
                            </Text>
                            <View
                                style={[
                                    stylesheet.row,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Avatar
                                    title={t("blocks.course01.teacher")}
                                    size="xSmall"
                                />
                                <Text
                                    variant="bodySmallSize"
                                    color="mid"
                                >
                                    {t("blocks.course01.teacherMeta", [
                                        getLessonMinutes(currentModule, Number(currentIndex) - 1)
                                    ])}
                                </Text>
                            </View>
                        </View>
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    gap: spaces.spacingSm
                                }
                            ]}
                        >
                            <Button
                                title={t("blocks.course01.previous")}
                                spreadBehaviour="free"
                                onPress={handlePress}
                                icon={PreviousIcon}
                                variant="outline"
                                type="neutral"
                            />
                            <Button
                                title={t("blocks.course01.complete")}
                                spreadBehaviour="free"
                                onPress={handlePress}
                                variant="filled"
                                icon={NextIcon}
                                type="primary"
                            />
                        </View>
                    </View>
                    <Tabs
                        onValueChange={handleTabChange}
                        overflowBehaviour="scroll"
                        variant="line"
                        value={tab}
                    >
                        <Tabs.List
                            title={t("blocks.course01.tabsTitle")}
                        >
                            {
                                TABS.map((key) => <Tabs.Trigger
                                    title={t(`blocks.course01.tab_${key}`)}
                                    count={TAB_COUNTS[key]}
                                    value={key}
                                    key={key}
                                />)
                            }
                        </Tabs.List>
                    </Tabs>
                    {renderTabContent()}
                </View>
                <View
                    style={[
                        stylesheet.side,
                        {
                            width: isWide ? shapeConfigs.cardWidth : "100%",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Card
                        variant="outline"
                    >
                        <Card.Content>
                            <Progress
                                valueFormatter={(value, max) => percentFormat.format(value / max)}
                                description={t("blocks.course01.progressDescription")}
                                title={t("blocks.course01.progressTitle")}
                                value={COURSE_PERCENT}
                            />
                        </Card.Content>
                    </Card>
                    <Accordion
                        onValueChange={handleModuleChange}
                        expandBehaviour="single"
                        value={openModule}
                        variant="card"
                    >
                        {MODULES.map(renderModule)}
                    </Accordion>
                    <Card
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.course01.quizTitle")}
                            </Card.Title>
                            <Card.Action>
                                <Badge
                                    title={t("blocks.course01.quizStep")}
                                    variant="tonal"
                                    type="neutral"
                                    size="small"
                                />
                            </Card.Action>
                        </Card.Header>
                        <Card.Content>
                            <View
                                style={{
                                    gap: spaces.spacingMd
                                }}
                            >
                                <Text
                                    variant="titleSmallSize"
                                    color="high"
                                >
                                    {t("blocks.course01.question")}
                                </Text>
                                <RadioGroup
                                    aria-label={t("blocks.course01.question")}
                                    onValueChange={handleChoiceChange}
                                    value={answer}
                                >
                                    {CHOICES.map(renderChoice)}
                                </RadioGroup>
                                {renderQuizResult()}
                                <View
                                    style={[
                                        stylesheet.row,
                                        {
                                            justifyContent: "space-between",
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <Text
                                        style={stylesheet.flex}
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.course01.quizHint")}
                                    </Text>
                                    <Button
                                        title={t("blocks.course01.check")}
                                        isDisabled={answer === undefined}
                                        spreadBehaviour="free"
                                        onPress={handleCheck}
                                        variant="tonal"
                                        type="neutral"
                                    />
                                </View>
                            </View>
                        </Card.Content>
                    </Card>
                </View>
            </View>
        </View>
        <Separator/>
        <View
            style={[
                stylesheet.footer,
                {
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t("blocks.course01.copyright")}
            </Text>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Button
                    title={t("blocks.course01.footerPrivacy")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="link"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={t("blocks.course01.footerHelp")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="link"
                    type="neutral"
                    size="small"
                />
            </View>
        </View>
    </ScrollView>;
};
export default Course01;
